// Logica pura da pagina de Analytics: periodo (7d, 30d, personalizado),
// normalizacao do payload das RPCs agregadas e o grid do heatmap dia x hora.
// O banco ja agrega em America/Sao_Paulo; aqui so moldamos para os graficos.

export type AnalyticsPreset = "today" | "7d" | "30d" | "custom";

export interface AnalyticsSelection {
  readonly preset: AnalyticsPreset;
  /** YYYY-MM-DD, so no preset custom. */
  readonly from?: string;
  readonly to?: string;
}

export interface AnalyticsRange {
  readonly start: Date;
  readonly end: Date;
}

const DAY_MS = 24 * 60 * 60 * 1000;
export const MAX_RANGE_DAYS = 366;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

function parseDay(value: string | undefined): Date | null {
  if (!value || !ISO_DAY.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number) as [number, number, number];
  const date = new Date(y, m - 1, d);
  return date.getMonth() === m - 1 && date.getDate() === d ? date : null;
}

/** Resolve o intervalo. null = selecao personalizada invalida (datas faltando,
 *  invertidas, no futuro distante ou acima de MAX_RANGE_DAYS). */
export function resolveAnalyticsRange(selection: AnalyticsSelection, now: Date = new Date()): AnalyticsRange | null {
  if (selection.preset === "custom") {
    const from = parseDay(selection.from);
    const to = parseDay(selection.to);
    if (!from || !to || to < from) return null;
    if ((to.getTime() - from.getTime()) / DAY_MS + 1 > MAX_RANGE_DAYS) return null;
    return { start: startOfDay(from), end: endOfDay(to) };
  }
  const days = selection.preset === "today" ? 1 : selection.preset === "7d" ? 7 : 30;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
  return { start, end: endOfDay(now) };
}

export function toIsoDay(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** "2026-10-03" -> "03/10" */
export function shortDayLabel(isoDay: string): string {
  const [, m, d] = isoDay.split("-");
  return `${d}/${m}`;
}

// ---------------------------------------------------------------------------
// Payload das RPCs
// ---------------------------------------------------------------------------

export interface LabelCount {
  readonly label: string;
  readonly count: number;
}

export interface CityCount extends LabelCount {
  readonly region: string | null;
  readonly country: string | null;
}

export interface DailyPoint {
  readonly day: string;
  readonly views: number;
  readonly visitors: number;
}

export interface HeatCell {
  readonly dow: number;
  readonly hour: number;
  readonly count: number;
}

export interface ChannelSeries {
  readonly channel: string;
  readonly values: readonly number[];
}

export interface PlatformRow {
  readonly marketplace: string;
  readonly clicks: number;
  readonly links: number;
}

export interface ClickAnalytics {
  readonly views: number;
  readonly uniqueVisitors: number;
  readonly bounceRate: number | null;
  readonly daily: readonly DailyPoint[];
  readonly heatmap: readonly HeatCell[];
  readonly channels: readonly { channel: string; count: number }[];
  readonly channelSeries: readonly ChannelSeries[];
  readonly platforms: readonly PlatformRow[];
  readonly devices: readonly LabelCount[];
  readonly browsers: readonly LabelCount[];
  readonly oses: readonly LabelCount[];
  readonly countries: readonly LabelCount[];
  readonly cities: readonly CityCount[];
  readonly referrers: readonly LabelCount[];
}

type Raw = Record<string, unknown>;

function num(value: unknown): number {
  const n = typeof value === "string" ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) ? n : 0;
}

function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function list(value: unknown): Raw[] {
  return Array.isArray(value) ? value.filter((v): v is Raw => typeof v === "object" && v !== null) : [];
}

function labelCounts(value: unknown): LabelCount[] {
  return list(value).map((r) => ({ label: str(r["label"], "Outro"), count: num(r["count"]) }));
}

/** Normaliza o jsonb de get_click_analytics (defensivo: campo ausente vira vazio/0). */
export function parseClickAnalytics(payload: unknown): ClickAnalytics {
  const r: Raw = typeof payload === "object" && payload !== null ? (payload as Raw) : {};
  const unique = num(r["unique_visitors"]);
  return {
    views: num(r["views"]),
    uniqueVisitors: unique,
    bounceRate: unique > 0 ? Math.round((num(r["single_click_visitors"]) / unique) * 100) : null,
    daily: list(r["daily"]).map((d) => ({ day: str(d["day"]), views: num(d["views"]), visitors: num(d["visitors"]) })),
    heatmap: list(r["heatmap"]).map((h) => ({ dow: num(h["dow"]), hour: num(h["hour"]), count: num(h["count"]) })),
    channels: list(r["channels"]).map((c) => ({ channel: str(c["channel"], "Outros"), count: num(c["count"]) })),
    channelSeries: list(r["channel_series"]).map((c) => ({
      channel: str(c["channel"], "Outros"),
      values: Array.isArray(c["values"]) ? c["values"].map(num) : [],
    })),
    platforms: list(r["platforms"]).map((p) => ({ marketplace: str(p["marketplace"], "outros"), clicks: num(p["clicks"]), links: num(p["links"]) })),
    devices: labelCounts(r["devices"]),
    browsers: labelCounts(r["browsers"]),
    oses: labelCounts(r["oses"]),
    countries: labelCounts(r["countries"]),
    cities: list(r["cities"]).map((c) => ({
      label: str(c["label"], "Outro"),
      count: num(c["count"]),
      region: typeof c["region"] === "string" ? c["region"] : null,
      country: typeof c["country"] === "string" ? c["country"] : null,
    })),
    referrers: labelCounts(r["referrers"]),
  };
}

// ---------------------------------------------------------------------------
// Heatmap
// ---------------------------------------------------------------------------

export const WEEKDAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"] as const;

export interface HeatGrid {
  /** grid[dow][hour] */
  readonly grid: readonly (readonly number[])[];
  readonly max: number;
  readonly best: { readonly dow: number; readonly hour: number; readonly count: number } | null;
}

/** Monta o grid 7x24 e o melhor horario (maior contagem; empate = o mais cedo). */
export function buildHeatGrid(cells: readonly HeatCell[]): HeatGrid {
  const grid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
  for (const c of cells) {
    if (c.dow >= 0 && c.dow < 7 && c.hour >= 0 && c.hour < 24) {
      grid[c.dow]![c.hour]! += c.count;
    }
  }
  let best: HeatGrid["best"] = null;
  let max = 0;
  grid.forEach((row, dow) =>
    row.forEach((count, hour) => {
      if (count > max) {
        max = count;
        best = { dow, hour, count };
      }
    }),
  );
  return { grid, max, best };
}

/** Intensidade 0..4 para a escala de cor do heatmap. */
export function heatLevel(count: number, max: number): number {
  if (count <= 0 || max <= 0) return 0;
  return Math.min(4, Math.ceil((count / max) * 4));
}

export const DEVICE_LABELS: Record<string, string> = {
  desktop: "Desktop",
  mobile: "Celular",
  tablet: "Tablet",
  bot: "Robôs de prévia",
  other: "Outros",
};

export function percent(value: number, total: number): number {
  return total > 0 ? Math.round((value / total) * 100) : 0;
}

// ---------------------------------------------------------------------------
// Membros e inscritos
// ---------------------------------------------------------------------------

export interface MemberGroupRow {
  readonly chatId: string;
  readonly title: string | null;
  readonly joins: number;
  readonly leaves: number;
  readonly members: number | null;
  readonly membersAt: string | null;
}

export interface SubscriberSeries {
  readonly chatId: string;
  readonly title: string | null;
  readonly points: readonly { day: string; count: number }[];
}

export interface MemberAnalytics {
  readonly joins: number;
  readonly leaves: number;
  readonly net: number;
  readonly groupsWithData: number;
  readonly daily: readonly { day: string; joins: number; leaves: number }[];
  readonly groups: readonly MemberGroupRow[];
  readonly subscribers: readonly SubscriberSeries[];
  readonly lastSnapshotAt: string | null;
}

export function parseMemberAnalytics(payload: unknown): MemberAnalytics {
  const r: Raw = typeof payload === "object" && payload !== null ? (payload as Raw) : {};
  const joins = num(r["joins"]);
  const leaves = num(r["leaves"]);
  return {
    joins,
    leaves,
    net: joins - leaves,
    groupsWithData: num(r["groups_with_data"]),
    daily: list(r["daily"]).map((d) => ({ day: str(d["day"]), joins: num(d["joins"]), leaves: num(d["leaves"]) })),
    groups: list(r["groups"]).map((g) => ({
      chatId: str(g["chat_id"]),
      title: typeof g["title"] === "string" ? g["title"] : null,
      joins: num(g["joins"]),
      leaves: num(g["leaves"]),
      members: g["members"] === null || g["members"] === undefined ? null : num(g["members"]),
      membersAt: typeof g["members_at"] === "string" ? g["members_at"] : null,
    })),
    subscribers: list(r["subscribers"]).map((s) => ({
      chatId: str(s["chat_id"]),
      title: typeof s["title"] === "string" ? s["title"] : null,
      points: list(s["points"]).map((p) => ({ day: str(p["day"]), count: num(p["count"]) })),
    })),
    lastSnapshotAt: typeof r["last_snapshot_at"] === "string" ? r["last_snapshot_at"] : null,
  };
}
